// Free plan vs teacher plan (D-090, migration 0038). The database triggers enforce the same
// class/student limits and species pool; keep these constants in sync with 0038.
import type { PetSpecies } from "./pet-modes";

export type PlanTier = "free" | "teacher";
/** What a workspace holds, finer than PlanTier: `basic` is the teacher plan, `plus` the larger one (D-114). */
export type PlanLevel = "free" | "basic" | "plus";

/** Access states a class or workspace can be in. `over_limit` is a free-plan class beyond the
 * free quota (a second class, or more than 40 students): it can be viewed and tidied up
 * (archive students or classes) but not scored until it fits or the teacher upgrades. */
export type AccessState = "trial" | "active" | "free" | "over_limit" | "read_only" | "suspended";

export const FREE_PLAN_LIMITS = { activeClassLimit: 1, studentsPerClassLimit: 40 } as const;
/** Teacher plan (basic) limits. */
export const TEACHER_PLAN_LIMITS = { activeClassLimit: 5, studentsPerClassLimit: 100 } as const;
export const PLUS_PLAN_LIMITS = { activeClassLimit: 10, studentsPerClassLimit: 100 } as const;
/** Same numbers as the quota triggers of migrations 0054 (classes) and 0076 (students); keep them in sync. Plus = 10 classes x 100 (D-190). */
export const PLAN_LIMITS = { free: FREE_PLAN_LIMITS, basic: TEACHER_PLAN_LIMITS, plus: PLUS_PLAN_LIMITS } as const;
export const planLevelFromRank = (rank: number | null | undefined): PlanLevel => rank === 2 ? "plus" : rank === 1 ? "basic" : "free";

/** New individual pets on the free plan come from these species; existing pets keep theirs. */
export const FREE_PET_SPECIES = [
  "otter", "rabbit", "penguin", "fox", "corgi", "cat",
  "red-panda", "panda", "capybara", "owl", "squirrel", "seal",
] as const satisfies readonly PetSpecies[];

/** First annual purchase of a workspace: applied automatically at checkout when no code is typed. */
export const FIRST_YEAR_DISCOUNT_CODE = "FIRSTYEAR";
export const FIRST_YEAR_PLUS_DISCOUNT_CODE = "FIRSTYEAR-PLUS";
/** Catalog ids of the two annual products. */
export const BASIC_ANNUAL_CATALOG_ID = "teacher-annual";
export const PLUS_ANNUAL_CATALOG_ID = "teacher-plus-annual";
/** Catalog ids of the two monthly products (Plus monthly since D-208, migration 0078). */
export const BASIC_MONTHLY_CATALOG_ID = "teacher-monthly";
export const PLUS_MONTHLY_CATALOG_ID = "teacher-plus-monthly";
export const firstYearCodeFor = (catalogId: string): string | null =>
  catalogId === BASIC_ANNUAL_CATALOG_ID ? FIRST_YEAR_DISCOUNT_CODE : catalogId === PLUS_ANNUAL_CATALOG_ID ? FIRST_YEAR_PLUS_DISCOUNT_CODE : null;

export const planTierFor = (state: AccessState): PlanTier =>
  state === "trial" || state === "active" ? "teacher" : "free";

/** Class themes and pet scenes available without the teacher plan. */
export const FREE_THEME_ID = "forest";
export const FREE_SCENE_ID = "forest";

/** AI comments (AI 評語, D-093): teacher plan only, server-enforced. Each workspace may generate at
 *  most `dailyStudentsPerWorkspace` student comments per Taipei day (reserved atomically before the
 *  model is called), at most `studentsPerRequest` students in one request. */
export const AI_COMMENT_LIMITS = { dailyStudentsPerWorkspace: 150, studentsPerRequest: 40 } as const;
